import { ApplicationController } from 'stimulus-use';

/**
 * GPE (NC34): collapse / expand the projects list hierarchy.
 *
 * The projects list has no collapse of its own: every subproject is always
 * shown. The rows arrive already flattened in depth-first order, each carrying
 * its depth (`idnt-N`, root = 0) and a `parent`/`leaf` class, but no reference
 * to its parent row. This controller rebuilds the parent of every row from the
 * depth-first order and the depth, turns each parent row's hierarchy marker
 * into a chevron toggle, and hides a subtree by walking the flat list.
 *
 * It listens on the window for `gpe:projects-tree` (detail: { collapse }) so the
 * toolbar button that lives in the sub-header - a different component - can drive
 * expand-all / collapse-all without a Stimulus outlet across the two frames.
 */
export default class OpProjectCollapseController extends ApplicationController {
  private rows:HTMLElement[] = [];
  private parentOf = new Map<HTMLElement, HTMLElement | null>();
  private collapsed = new Set<HTMLElement>();
  private boundToggleAll = this.onToggleAll.bind(this);

  connect() {
    this.indexRows();
    this.decorateParents();
    window.addEventListener('gpe:projects-tree', this.boundToggleAll);
  }

  disconnect() {
    super.disconnect();
    window.removeEventListener('gpe:projects-tree', this.boundToggleAll);
    this.rows.forEach((row) => {
      const toggle = row.querySelector('.gpe-tree-toggle');
      if (toggle) { toggle.remove(); }
    });
  }

  // Build the flat, ordered row list and, from the depth-first order + depth,
  // the parent of every row (nearest preceding row one level shallower).
  private indexRows() {
    this.rows = Array.from(
      this.element.querySelectorAll<HTMLElement>('tr.op-project-row-component'),
    );

    const stack:HTMLElement[] = [];
    this.rows.forEach((row) => {
      const level = this.levelOf(row);
      stack.length = level; // keep ancestors shallower than this row
      this.parentOf.set(row, level > 0 ? (stack[level - 1] ?? null) : null);
      stack[level] = row;
    });
  }

  private levelOf(row:HTMLElement):number {
    const match = row.className.match(/\bidnt-(\d+)\b/);
    return match ? parseInt(match[1], 10) : 0;
  }

  private isParent(row:HTMLElement):boolean {
    return row.classList.contains('parent');
  }

  // Give every parent row a chevron in front of its hierarchy marker.
  private decorateParents() {
    this.rows.filter((row) => this.isParent(row)).forEach((row) => {
      const marker = row.querySelector<HTMLElement>('.projects-table--hierarchy-icon');
      if (!marker || marker.parentElement?.querySelector('.gpe-tree-toggle')) { return; }

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'gpe-tree-toggle';
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Expandir ou colapsar subprojectos');
      toggle.innerHTML = this.chevronSvg();
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleRow(row);
      });
      marker.parentElement?.insertBefore(toggle, marker);
    });
  }

  private chevronSvg():string {
    return '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">'
      + '<path fill="currentColor" d="M6.22 4.22a.75.75 0 0 1 1.06 0l3.25 3.25a.75.75 0 0 1 0 1.06l-3.25 3.25a.75.75 0 1 1-1.06-1.06L8.94 8 6.22 5.28a.75.75 0 0 1 0-1.06Z"/>'
      + '</svg>';
  }

  private toggleRow(row:HTMLElement) {
    if (this.collapsed.has(row)) {
      this.collapsed.delete(row);
    } else {
      this.collapsed.add(row);
    }
    this.applyVisibility();
  }

  // A row is hidden when any of its ancestors is collapsed.
  private applyVisibility() {
    this.rows.forEach((row) => {
      let hidden = false;
      let ancestor = this.parentOf.get(row) ?? null;
      while (ancestor) {
        if (this.collapsed.has(ancestor)) { hidden = true; break; }
        ancestor = this.parentOf.get(ancestor) ?? null;
      }
      row.classList.toggle('-gpe-collapsed-row', hidden);

      if (this.isParent(row)) {
        const expanded = !this.collapsed.has(row);
        row.classList.toggle('-gpe-collapsed', !expanded);
        const toggle = row.querySelector('.gpe-tree-toggle');
        if (toggle) { toggle.setAttribute('aria-expanded', String(expanded)); }
      }
    });
  }

  private onToggleAll(event:Event) {
    const collapse = (event as CustomEvent<{ collapse:boolean }>).detail?.collapse;
    if (collapse) {
      this.rows.filter((row) => this.isParent(row)).forEach((row) => this.collapsed.add(row));
    } else {
      this.collapsed.clear();
    }
    this.applyVisibility();
  }
}
